import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { RequireAuth } from "@/components/RequireAuth";
import { AppShell } from "@/components/AppShell";
import { ScheduleTabs } from "@/components/schedule/ScheduleTabs";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { buildAppMeta, SITE_URL } from "@/lib/seo";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  type BookingSettings,
  type CoachBooking,
  type LocationWindow,
  fmtBookingDate,
  fmtSlotTime,
  publicBookingUrl,
  slugError,
  WEEKDAY_FULL,
} from "@/lib/booking";
import { ArrowRight, Check, Copy, ExternalLink, Loader2, MapPin, RefreshCw, X } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/app/bookings")({
  head: () => ({
    meta: buildAppMeta({
      title: "Bookings — CoachQuick",
      description:
        "Let clients book lessons on your public booking page with cascading availability.",
      path: "/app/bookings",
    }),
    links: [{ rel: "canonical", href: `${SITE_URL}/app/bookings` }],
  }),
  component: () => (
    <RequireAuth role="coach">
      <BookingsPage />
    </RequireAuth>
  ),
});

const TIMEZONES = [
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Phoenix",
  "America/Los_Angeles",
  "America/Anchorage",
  "Pacific/Honolulu",
];

const DEFAULT_WINDOW = {
  total_start: "09:00",
  total_end: "17:00",
  preferred_start: "12:00",
  preferred_end: "15:00",
};

type LocationLite = { id: string; name: string };

function detectTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "America/New_York";
  } catch {
    return "America/New_York";
  }
}

/** "14:30:00" -> "14:30" for <input type="time"> */
const t5 = (t: string) => t.slice(0, 5);

/** Decimal hours between two "HH:MM" times. */
function hoursBetween(a: string, b: string): number {
  const [ah, am] = a.split(":").map(Number);
  const [bh, bm] = b.split(":").map(Number);
  return (bh * 60 + bm - (ah * 60 + am)) / 60;
}

/** Whole number when clean, one decimal otherwise (6, 6.5). */
function fmtHrs(h: number): string {
  const r = Math.round(h * 10) / 10;
  return Number.isInteger(r) ? String(r) : r.toFixed(1);
}

const timeInputCls =
  "h-9 min-w-[92px] flex-1 rounded-[10px] border border-input bg-card px-3 text-sm text-foreground outline-none focus:ring-1 focus:ring-ring [font-variant-numeric:tabular-nums]";

function BookingsPage() {
  const { user } = useAuth();
  const qc = useQueryClient();

  // ---- Queries ------------------------------------------------------------

  const settingsQ = useQuery({
    queryKey: ["booking-settings", user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("coach_booking_settings")
        .select("*")
        .eq("coach_id", user!.id)
        .maybeSingle();
      if (error) throw error;
      return (data as unknown as BookingSettings | null) ?? null;
    },
  });

  const locationsQ = useQuery({
    queryKey: ["booking-locations", user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("coach_locations")
        .select("id, name")
        .eq("coach_id", user!.id)
        .order("name");
      if (error) throw error;
      return (data as LocationLite[]) ?? [];
    },
  });

  const windowsQ = useQuery({
    queryKey: ["booking-windows", user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("coach_location_windows")
        .select("*")
        .eq("coach_id", user!.id);
      if (error) throw error;
      return (data as unknown as LocationWindow[]) ?? [];
    },
  });

  const todayIso = new Date().toISOString().slice(0, 10);
  const bookingsQ = useQuery({
    queryKey: ["coach-bookings", user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("coach_bookings")
        .select("*")
        .eq("coach_id", user!.id)
        .gte("booking_date", todayIso)
        .in("status", ["pending", "confirmed"])
        .order("booking_date")
        .order("start_time");
      if (error) throw error;
      return (data as unknown as CoachBooking[]) ?? [];
    },
  });

  const settings = settingsQ.data;
  const locations = useMemo(() => locationsQ.data ?? [], [locationsQ.data]);
  const windows = windowsQ.data ?? [];
  const bookings = bookingsQ.data ?? [];
  const locName = useMemo(() => new Map(locations.map((l) => [l.id, l.name])), [locations]);

  // ---- Mutations ----------------------------------------------------------

  const patchSettings = useMutation({
    mutationFn: async (patch: Partial<BookingSettings>) => {
      const { error } = await supabase
        .from("coach_booking_settings")
        .upsert({ coach_id: user!.id, ...patch } as never, { onConflict: "coach_id" });
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["booking-settings", user?.id] }),
    onError: (e) => toast.error(e instanceof Error ? e.message : "Failed to save"),
  });

  async function enablePage(next: boolean) {
    if (!settings && next) {
      await patchSettings.mutateAsync({ enabled: true, timezone: detectTimezone() });
      toast.success("Booking page is live");
      return;
    }
    await patchSettings.mutateAsync({ enabled: next });
    toast.success(next ? "Now accepting bookings" : "Bookings paused");
  }

  async function regenerateToken() {
    const newToken = crypto.randomUUID().replace(/-/g, "");
    await patchSettings.mutateAsync({ public_token: newToken });
    toast.success("New link generated. The old link no longer works.");
  }

  const upsertWindow = useMutation({
    mutationFn: async (w: {
      location_id: string;
      weekday: number;
      total_start: string;
      total_end: string;
      preferred_start: string;
      preferred_end: string;
      active: boolean;
    }) => {
      const { error } = await supabase
        .from("coach_location_windows")
        .upsert({ coach_id: user!.id, ...w } as never, { onConflict: "location_id,weekday" });
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["booking-windows", user?.id] }),
    onError: (e) => toast.error(e instanceof Error ? e.message : "Failed to save hours"),
  });

  const setBookingStatus = useMutation({
    mutationFn: async (vars: { id: string; status: "confirmed" | "declined" | "cancelled" }) => {
      const patch: Record<string, unknown> = { status: vars.status };
      if (vars.status === "cancelled" || vars.status === "declined") {
        patch.cancelled_at = new Date().toISOString();
        patch.cancelled_by = "coach";
      }
      const { error } = await supabase
        .from("coach_bookings")
        .update(patch as never)
        .eq("id", vars.id);
      if (error) throw error;
    },
    onSuccess: (_d, vars) => {
      toast.success(
        vars.status === "confirmed"
          ? "Booking confirmed"
          : vars.status === "declined"
            ? "Request declined"
            : "Booking cancelled",
      );
      qc.invalidateQueries({ queryKey: ["coach-bookings", user?.id] });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Failed"),
  });

  // ---- Derived ------------------------------------------------------------

  // Always show the published, public URL so a coach never shares a
  // preview-host link. Prefer the friendly slug.
  const slugOrToken = settings?.booking_slug || settings?.public_token || "";
  const url = settings?.public_token
    ? publicBookingUrl(settings.booking_slug || settings.public_token)
    : "";
  const configured = !!settings?.public_token;
  const accepting = settings?.enabled ?? false;
  const pending = bookings.filter((b) => b.status === "pending");
  const upcoming = bookings.filter((b) => b.status === "confirmed");
  const upcomingLocationCount = new Set(upcoming.map((b) => b.location_id)).size;
  const hasAnyWindow = windows.some((w) => w.active);
  // The claim-your-link field appears once the slug migration has run.
  const slugSupported = !!settings && "booking_slug" in settings;

  async function copyUrl() {
    await navigator.clipboard.writeText(url);
    toast.success("Link copied");
  }

  async function saveSlug(raw: string) {
    const slug = raw.trim().toLowerCase();
    if (slug === (settings?.booking_slug ?? "")) return;
    if (slug !== "") {
      const err = slugError(slug);
      if (err) {
        toast.error(err);
        return;
      }
    }
    const { error } = await supabase
      .from("coach_booking_settings")
      .update({ booking_slug: slug || null } as never)
      .eq("coach_id", user!.id);
    if (error) {
      const msg = error.message ?? "";
      if (msg.includes("duplicate key") || msg.includes("unique")) {
        toast.error("That link name is already taken — try another.");
      } else if (msg.includes("check constraint") || msg.includes("violates")) {
        toast.error("That name isn't allowed — try another.");
      } else {
        toast.error(msg || "Couldn't save the link name");
      }
      return;
    }
    toast.success(slug ? `Your link is now coachquick.com/book/${slug}` : "Custom link removed");
    qc.invalidateQueries({ queryKey: ["booking-settings", user?.id] });
  }

  const isLoading = settingsQ.isLoading || locationsQ.isLoading || windowsQ.isLoading;

  return (
    <AppShell>
      <div className="-mx-4 -my-6 min-h-[calc(100vh-4rem)] bg-canvas-soft px-4 py-6 md:-mx-8 md:px-8">
        <div>
          <h1 className="font-display text-4xl font-black tracking-tight text-foreground md:text-5xl">
            Schedule
          </h1>
          <div className="mt-4 flex flex-wrap items-center gap-4">
            <ScheduleTabs />
            <p className="text-sm text-muted-foreground">
              Booked lessons show up on the{" "}
              <span className="font-medium text-foreground">Calendar</span> tab.
            </p>
          </div>
        </div>

        {isLoading ? (
          <div className="mt-8 flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : (
          <>
            {/* ------------------------------------------------------- Hero */}
            <section className="mt-6 flex flex-col gap-5 rounded-[20px] bg-ink-panel p-4 text-white sm:p-6">
              <div className="flex flex-wrap items-center justify-between gap-4 border-b border-white/12 pb-4">
                <div className="flex items-center gap-2.5">
                  <p className="text-[11px] font-semibold tracking-[0.18em] text-white/60">
                    YOUR BOOKING PAGE
                  </p>
                  {configured &&
                    (accepting ? (
                      <span className="inline-flex items-center rounded-full bg-primary px-2.5 py-0.5 text-[11px] font-bold tracking-[0.06em] text-primary-foreground">
                        LIVE
                      </span>
                    ) : (
                      <span className="inline-flex items-center rounded-full bg-white/[0.14] px-2.5 py-0.5 text-[11px] font-bold tracking-[0.06em] text-white/85">
                        PAUSED
                      </span>
                    ))}
                </div>
                <label className="flex cursor-pointer items-center gap-2.5">
                  <span className="text-sm font-semibold text-white">Accepting bookings</span>
                  <Switch
                    checked={accepting}
                    disabled={patchSettings.isPending}
                    onCheckedChange={enablePage}
                    aria-label="Accepting bookings"
                  />
                </label>
              </div>

              {configured ? (
                <>
                  <div className="flex flex-wrap items-center gap-x-10 gap-y-7">
                    <div className="min-w-0 flex-[1_1_460px]">
                      {/* Link + actions */}
                      <div className="flex flex-wrap items-center gap-2">
                        <div className="flex h-10 max-w-full items-center overflow-hidden whitespace-nowrap rounded-[10px] border border-white/16 bg-white/[0.06] px-3.5 font-mono text-sm text-white">
                          coachquick.com/book/<span className="text-primary">{slugOrToken}</span>
                        </div>
                        <button
                          type="button"
                          onClick={copyUrl}
                          className="inline-flex h-10 items-center gap-1.5 rounded-[10px] border border-white/16 bg-white/[0.06] px-3.5 text-sm font-semibold text-white transition hover:bg-white/[0.12]"
                        >
                          <Copy className="h-[15px] w-[15px]" /> Copy link
                        </button>
                        <a
                          href={url}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex h-10 items-center gap-1.5 rounded-[10px] border border-white/16 bg-white/[0.06] px-3.5 text-sm font-semibold text-white transition hover:bg-white/[0.12]"
                        >
                          <ExternalLink className="h-[15px] w-[15px]" /> Open
                        </a>
                        <button
                          type="button"
                          onClick={regenerateToken}
                          disabled={patchSettings.isPending}
                          className="inline-flex h-10 items-center gap-1.5 rounded-[10px] px-2.5 text-sm font-semibold text-white/70 transition hover:text-white disabled:opacity-50"
                        >
                          <RefreshCw className="h-3.5 w-3.5" /> Regenerate
                        </button>
                      </div>

                      {/* Custom link */}
                      {slugSupported && (
                        <div className="mt-4 border-t border-white/12 pt-4">
                          <p className="text-[11px] font-semibold tracking-[0.18em] text-white/60">
                            CUSTOM LINK
                          </p>
                          <HeroSlugEditor
                            key={settings?.booking_slug ?? ""}
                            initialSlug={settings?.booking_slug ?? ""}
                            onSave={saveSlug}
                          />
                          <p className="mt-2.5 text-sm text-white/70">
                            Your long link keeps working either way. Regenerating doesn't change
                            this name.
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Stats */}
                    <div className="flex flex-wrap gap-x-10 gap-y-6">
                      <div>
                        <p className="text-[11px] font-semibold tracking-[0.18em] text-white/60">
                          WAITING ON YOU
                        </p>
                        <p className="mt-2 font-display text-5xl font-black leading-none text-primary md:text-6xl">
                          {pending.length}
                        </p>
                        <p className="mt-1.5 text-sm text-white/70">
                          {pending.length === 1 ? "request to confirm" : "requests to confirm"}
                        </p>
                      </div>
                      <div>
                        <p className="text-[11px] font-semibold tracking-[0.18em] text-white/60">
                          BOOKED AHEAD
                        </p>
                        <Link
                          to="/app/calendar"
                          className="mt-2 block font-display text-5xl font-black leading-none text-white md:text-6xl"
                        >
                          {upcoming.length}
                        </Link>
                        <Link
                          to="/app/calendar"
                          className="mt-1.5 inline-flex items-center gap-1 text-sm text-white/70 transition hover:text-white"
                        >
                          {upcoming.length === 1 ? "lesson" : "lessons"}
                          {upcomingLocationCount > 0 &&
                            ` · ${upcomingLocationCount} ${upcomingLocationCount === 1 ? "location" : "locations"}`}
                          <ArrowRight className="h-3.5 w-3.5" />
                        </Link>
                      </div>
                    </div>
                  </div>

                  {!hasAnyWindow && (
                    <p className="border-t border-white/12 pt-4 text-sm text-white/70">
                      Your page is on, but you haven't set any available hours yet — add them below
                      so clients can see open times.
                    </p>
                  )}

                  {!accepting && (
                    <p className="border-t border-white/12 pt-4 text-sm text-white/70 [text-wrap:pretty]">
                      Your page is up but not taking requests. Visitors see{" "}
                      <span className="text-white">“Not booking right now — check back soon.”</span>{" "}
                      Your link still works, confirmed lessons are unaffected, and nothing is
                      deleted.
                    </p>
                  )}
                </>
              ) : (
                <p className="text-sm text-white/70 [text-wrap:pretty]">
                  Flip this on to publish a booking page at{" "}
                  <span className="font-mono text-white">coachquick.com/book/…</span> — a shareable
                  link clients use to request lessons in your available hours.
                </p>
              )}
            </section>

            {/* --------------------------------------------- Pending requests */}
            {accepting && pending.length > 0 && (
              <section className="mt-5 rounded-[20px] border border-primary/45 bg-accent p-3.5 sm:p-5">
                <h2 className="text-base font-bold tracking-[-0.01em]">
                  Requests waiting for you ({pending.length})
                </h2>
                <div className="mt-3 flex flex-col gap-2">
                  {pending.map((b) => (
                    <div
                      key={b.id}
                      className="flex flex-wrap items-center justify-between gap-x-4 gap-y-3 rounded-[12px] border bg-card p-3 sm:px-4"
                    >
                      <div className="min-w-0">
                        <p className="text-sm font-semibold">{b.booker_name}</p>
                        <p className="mt-0.5 text-sm text-muted-foreground">
                          {fmtBookingDate(b.booking_date, { weekday: "short", month: "short" })} ·{" "}
                          {fmtSlotTime(b.start_time)} · {locName.get(b.location_id) ?? "Location"}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          onClick={() => setBookingStatus.mutate({ id: b.id, status: "confirmed" })}
                          disabled={setBookingStatus.isPending}
                        >
                          <Check className="h-[15px] w-[15px]" /> Confirm
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setBookingStatus.mutate({ id: b.id, status: "declined" })}
                          disabled={setBookingStatus.isPending}
                        >
                          <X className="h-[15px] w-[15px]" /> Decline
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* -------------------------------------------------- Booking rules */}
            {configured && settings && (
              <section className="mt-5 rounded-[20px] border bg-card p-4 sm:p-6">
                <h2 className="text-base font-bold tracking-[-0.01em]">Booking rules</h2>
                <p className="mt-1 text-sm text-muted-foreground">
                  How far ahead clients can book, and how long each lesson runs.
                </p>
                <div className="mt-5 grid grid-cols-[repeat(auto-fit,minmax(190px,1fr))] gap-4">
                  <RuleField label="Lesson length">
                    <Select
                      value={String(settings.slot_minutes)}
                      onValueChange={(v) => patchSettings.mutate({ slot_minutes: Number(v) })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[30, 45, 60, 90, 120].map((m) => (
                          <SelectItem key={m} value={String(m)}>
                            {m} minutes
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </RuleField>
                  <RuleField label="Minimum notice">
                    <Select
                      value={String(settings.min_notice_hours)}
                      onValueChange={(v) => patchSettings.mutate({ min_notice_hours: Number(v) })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[0, 2, 6, 12, 24, 48].map((h) => (
                          <SelectItem key={h} value={String(h)}>
                            {h === 0 ? "No minimum" : `${h} hours`}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </RuleField>
                  <RuleField label="Book up to">
                    <Select
                      value={String(settings.max_advance_days)}
                      onValueChange={(v) => patchSettings.mutate({ max_advance_days: Number(v) })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[7, 14, 21, 30, 60].map((d) => (
                          <SelectItem key={d} value={String(d)}>
                            {d} days ahead
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </RuleField>
                  <RuleField label="Max lessons per location/day">
                    <Select
                      value={String(settings.max_per_location_per_day)}
                      onValueChange={(v) =>
                        patchSettings.mutate({ max_per_location_per_day: Number(v) })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[1, 2, 3, 4, 5, 6].map((n) => (
                          <SelectItem key={n} value={String(n)}>
                            {n}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </RuleField>
                  <RuleField label="Timezone">
                    <Select
                      value={settings.timezone}
                      onValueChange={(v) => patchSettings.mutate({ timezone: v })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {(TIMEZONES.includes(settings.timezone)
                          ? TIMEZONES
                          : [settings.timezone, ...TIMEZONES]
                        ).map((tz) => (
                          <SelectItem key={tz} value={tz}>
                            {tz.replace(/_/g, " ")}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </RuleField>
                </div>

                <div className="mt-5 flex flex-wrap gap-x-12 gap-y-4 border-t pt-5">
                  <label className="flex cursor-pointer items-center gap-3">
                    <Switch
                      checked={settings.auto_confirm}
                      onCheckedChange={(v) => patchSettings.mutate({ auto_confirm: v })}
                    />
                    <span>
                      <span className="block text-sm font-semibold">Auto-confirm bookings</span>
                      <span className="block text-sm text-muted-foreground">
                        Off = you approve each request
                      </span>
                    </span>
                  </label>
                  <label className="flex cursor-pointer items-center gap-3">
                    <Switch
                      checked={settings.allow_new_clients}
                      onCheckedChange={(v) => patchSettings.mutate({ allow_new_clients: v })}
                    />
                    <span>
                      <span className="block text-sm font-semibold">Allow new clients</span>
                      <span className="block text-sm text-muted-foreground">
                        Off = existing clients only
                      </span>
                    </span>
                  </label>
                </div>
              </section>
            )}

            {/* ---------------------------------------------- Available hours */}
            <section className="mt-5 rounded-[20px] border bg-card p-4 sm:p-6">
              <h2 className="flex items-center gap-2 text-base font-bold tracking-[-0.01em]">
                <MapPin className="h-[18px] w-[18px] text-brand-text" /> Available hours by location
              </h2>
              <p className="mt-1.5 max-w-[64ch] text-sm text-muted-foreground [text-wrap:pretty]">
                <span className="font-medium text-foreground">Preferred hours</span> are offered
                first. The wider window only opens up next to booked lessons — that's the cascade.
              </p>
              {locations.length === 0 ? (
                <p className="mt-4 text-sm text-muted-foreground">
                  Add a location first on the{" "}
                  <a
                    href="/app/locations"
                    target="_blank"
                    rel="noreferrer"
                    className="font-medium text-brand-text underline underline-offset-2"
                  >
                    Locations page
                  </a>
                  .
                </p>
              ) : (
                <div className="mt-6 flex flex-col gap-8">
                  {locations.map((loc) => (
                    <LocationWindowsEditor
                      key={loc.id}
                      location={loc}
                      windows={windows.filter((w) => w.location_id === loc.id)}
                      onSave={(w) => upsertWindow.mutate(w)}
                      saving={upsertWindow.isPending}
                    />
                  ))}
                </div>
              )}
              <p className="mt-6 text-sm text-muted-foreground">
                Coaching somewhere new? Add it on the{" "}
                <a
                  href="/app/locations"
                  target="_blank"
                  rel="noreferrer"
                  className="font-medium text-brand-text underline underline-offset-2"
                >
                  Locations page
                </a>
                .
              </p>
            </section>
          </>
        )}
      </div>
    </AppShell>
  );
}

function RuleField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-sm font-semibold">{label}</label>
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Custom-link (slug) editor, styled for the dark hero panel
// ---------------------------------------------------------------------------
function HeroSlugEditor({
  initialSlug,
  onSave,
}: {
  initialSlug: string;
  onSave: (slug: string) => void;
}) {
  const [value, setValue] = useState(initialSlug);
  const dirty = value.trim().toLowerCase() !== initialSlug;
  const err = value.trim() ? slugError(value.trim().toLowerCase()) : null;

  return (
    <div>
      <div className="mt-2.5 flex flex-wrap items-center gap-2">
        <span className="font-mono text-sm text-white/60">coachquick.com/book/</span>
        <input
          value={value}
          onChange={(e) => setValue(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))}
          spellCheck={false}
          aria-label="Custom link name"
          placeholder="your-name"
          className="h-10 min-w-[140px] max-w-[240px] flex-[1_1_180px] rounded-[10px] border border-white/16 bg-white/[0.06] px-3 font-mono text-sm text-white outline-none placeholder:text-white/40 focus:ring-1 focus:ring-primary"
        />
        <button
          type="button"
          disabled={!dirty || !!err}
          onClick={() => onSave(value)}
          className="inline-flex h-10 items-center rounded-[10px] bg-primary px-4 text-sm font-semibold text-primary-foreground transition hover:opacity-90 disabled:opacity-40"
        >
          Save
        </button>
        {initialSlug !== "" && (
          <button
            type="button"
            onClick={() => {
              setValue("");
              onSave("");
            }}
            className="inline-flex h-10 items-center rounded-[10px] px-2.5 text-sm font-semibold text-white/70 transition hover:text-white"
          >
            Remove
          </button>
        )}
      </div>
      {err && dirty && <p className="mt-1.5 text-sm text-destructive-foreground/90">{err}</p>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Weekday windows editor for a single location
// ---------------------------------------------------------------------------
function LocationWindowsEditor({
  location,
  windows,
  onSave,
  saving,
}: {
  location: LocationLite;
  windows: LocationWindow[];
  onSave: (w: {
    location_id: string;
    weekday: number;
    total_start: string;
    total_end: string;
    preferred_start: string;
    preferred_end: string;
    active: boolean;
  }) => void;
  saving: boolean;
}) {
  const byDay = useMemo(() => new Map(windows.map((w) => [w.weekday, w])), [windows]);
  // Order rows Mon..Sun for coach readability
  const dayOrder = [1, 2, 3, 4, 5, 6, 0];

  const activeWins = windows.filter((w) => w.active);
  const daysOpen = activeWins.length;
  const weekHrs = activeWins.reduce(
    (s, w) => s + hoursBetween(t5(w.total_start), t5(w.total_end)),
    0,
  );

  return (
    <div>
      <div className="flex items-baseline justify-between gap-4 pb-2">
        <p className="font-display text-lg font-extrabold tracking-[-0.02em]">{location.name}</p>
        <p className="text-sm text-muted-foreground">
          {daysOpen === 0
            ? "No days open yet"
            : `${daysOpen} ${daysOpen === 1 ? "day" : "days"} open · ${fmtHrs(weekHrs)} hrs a week`}
        </p>
      </div>

      {/* Desktop column headers */}
      <div className="hidden flex-wrap items-center gap-3 px-3 pb-1.5 md:flex">
        <span className="flex-[0_0_112px]" />
        <p className="max-w-[292px] flex-[1_1_250px] pl-2 text-[11px] font-semibold tracking-[0.18em] text-muted-foreground">
          WINDOW
        </p>
        <p className="max-w-[292px] flex-[1_1_250px] pl-2 text-[11px] font-semibold tracking-[0.18em] text-brand-text">
          PREFERRED
        </p>
        <span className="flex-[1_1_110px]" />
      </div>

      <div className="flex flex-col gap-1.5">
        {dayOrder.map((wd) => (
          <WeekdayRow
            key={wd}
            weekday={wd}
            existing={byDay.get(wd) ?? null}
            locationId={location.id}
            onSave={onSave}
            saving={saving}
          />
        ))}
      </div>
    </div>
  );
}

function WeekdayRow({
  weekday,
  existing,
  locationId,
  onSave,
  saving,
}: {
  weekday: number;
  existing: LocationWindow | null;
  locationId: string;
  onSave: (w: {
    location_id: string;
    weekday: number;
    total_start: string;
    total_end: string;
    preferred_start: string;
    preferred_end: string;
    active: boolean;
  }) => void;
  saving: boolean;
}) {
  const active = existing?.active ?? false;
  const [draft, setDraft] = useState({
    total_start: existing ? t5(existing.total_start) : DEFAULT_WINDOW.total_start,
    total_end: existing ? t5(existing.total_end) : DEFAULT_WINDOW.total_end,
    preferred_start: existing ? t5(existing.preferred_start) : DEFAULT_WINDOW.preferred_start,
    preferred_end: existing ? t5(existing.preferred_end) : DEFAULT_WINDOW.preferred_end,
  });

  function commit(next: Partial<typeof draft>, nextActive?: boolean) {
    const merged = { ...draft, ...next };
    setDraft(merged);
    const isActive = nextActive ?? active;
    // Validate before hitting the DB constraint
    if (isActive) {
      if (merged.total_start >= merged.total_end) {
        toast.error("Window end must be after start");
        return;
      }
      if (
        merged.preferred_start < merged.total_start ||
        merged.preferred_end > merged.total_end ||
        merged.preferred_start >= merged.preferred_end
      ) {
        toast.error("Preferred hours must sit inside the full window");
        return;
      }
    }
    onSave({ location_id: locationId, weekday, ...merged, active: isActive });
  }

  if (!active) {
    return (
      <div className="flex min-h-[54px] flex-wrap items-center gap-3 rounded-[14px] border border-transparent bg-muted px-3 py-2">
        <div className="flex flex-[0_0_112px] items-center gap-2.5">
          <Switch
            checked={false}
            disabled={saving}
            onCheckedChange={(v) => commit({}, v)}
            aria-label={`${WEEKDAY_FULL[weekday]} available`}
          />
          <span className="text-sm font-semibold text-muted-foreground">
            {WEEKDAY_FULL[weekday].slice(0, 3)}
          </span>
        </div>
        <p className="flex-1 text-sm text-muted-foreground">Not available</p>
      </div>
    );
  }

  const windowHrs = hoursBetween(draft.total_start, draft.total_end);
  const preferredHrs = hoursBetween(draft.preferred_start, draft.preferred_end);

  return (
    <div className="flex flex-wrap items-center gap-3 rounded-[14px] border bg-card px-3 py-2">
      <div className="flex flex-[0_0_112px] items-center gap-2.5">
        <Switch
          checked
          disabled={saving}
          onCheckedChange={(v) => commit({}, v)}
          aria-label={`${WEEKDAY_FULL[weekday]} available`}
        />
        <span className="text-sm font-semibold">{WEEKDAY_FULL[weekday].slice(0, 3)}</span>
      </div>

      {/* Window */}
      <div className="box-border flex max-w-[292px] min-w-0 flex-[1_1_250px] flex-wrap items-center gap-3 p-2">
        <span className="w-full pb-0.5 pl-0.5 text-[11px] font-semibold tracking-[0.18em] text-muted-foreground md:hidden">
          WINDOW
        </span>
        <input
          type="time"
          value={draft.total_start}
          onChange={(e) => setDraft((d) => ({ ...d, total_start: e.target.value }))}
          onBlur={(e) => commit({ total_start: e.target.value })}
          className={timeInputCls}
        />
        <input
          type="time"
          value={draft.total_end}
          onChange={(e) => setDraft((d) => ({ ...d, total_end: e.target.value }))}
          onBlur={(e) => commit({ total_end: e.target.value })}
          className={timeInputCls}
        />
      </div>

      {/* Preferred */}
      <div className="box-border flex max-w-[292px] min-w-0 flex-[1_1_250px] flex-wrap items-center gap-3 rounded-[12px] border border-primary/45 bg-accent p-2">
        <span className="w-full pb-0.5 pl-0.5 text-[11px] font-semibold tracking-[0.18em] text-brand-text md:hidden">
          PREFERRED
        </span>
        <input
          type="time"
          value={draft.preferred_start}
          onChange={(e) => setDraft((d) => ({ ...d, preferred_start: e.target.value }))}
          onBlur={(e) => commit({ preferred_start: e.target.value })}
          className={timeInputCls}
        />
        <input
          type="time"
          value={draft.preferred_end}
          onChange={(e) => setDraft((d) => ({ ...d, preferred_end: e.target.value }))}
          onBlur={(e) => commit({ preferred_end: e.target.value })}
          className={timeInputCls}
        />
      </div>

      <p className="flex-[1_1_110px] text-right text-sm text-muted-foreground [font-variant-numeric:tabular-nums]">
        {fmtHrs(windowHrs)} hrs · {fmtHrs(preferredHrs)} preferred
      </p>
    </div>
  );
}
